// ชิ้นส่วน UI แนวหรู (ทอง-ม่วงเข้ม) ที่ใช้ภาพตัวละครจาก manifest — ภาพอยู่ใน public/ ไม่ถูก bundle
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon, fmtNum } from "./ui";
import { imageById, imageFor, sizedSrc } from "../assets/characterImages";
import { personalize } from "./CharacterCard";
import "../styles/lux.css";

/** <img> ของภาพใน manifest — กำหนด width/height + aspect-ratio ไว้เสมอเพื่อกัน layout shift */
export function Pic({ img, size = "thumb", eager, className, alt = "" }) {
  if (!img) return null;
  const w = size === "thumb" ? 360 : size === "med" ? 720 : 853;
  const src = size === "thumb" ? img.thumb : size === "med" ? img.med : img.file;
  return (
    <img className={className} src={src} alt={alt} width={w} height={Math.round(w / (img.ratio || 0.667))} style={{ aspectRatio: String(img.ratio || 0.667) }}
      loading={eager ? "eager" : "lazy"} decoding="async" />
  );
}

/** คอลัมน์ภาพเลื่อนช้า ๆ (marquee แนวตั้ง) — ซ้ำรายการ 2 รอบเพื่อวนต่อเนื่อง ; reduced-motion หยุดเลื่อน (base.css) */
export function MarqueeColumn({ items, reverse, speed = 70, className = "" }) {
  return (
    <div className={`lux-col ${className}`} aria-hidden="true">
      <div className={`lux-col__track${reverse ? " is-rev" : ""}`} style={{ "--dur": `${speed}s` }}>
        {[0, 1].map((k) => (
          <div className="lux-col__set" key={k}>
            {items.map((img) => <Pic key={`${k}-${img.id}`} img={img} className="lux-col__img" eager={k === 0} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

/** ภาพประกอบหัวหน้า (coins/club/rewards): ใบหน้าซ้อนกัน 3 ใบ กรอบทองบาง ๆ */
export function BannerChars({ ids }) {
  const imgs = ids.map(imageById).filter(Boolean);
  return (
    <div className="lux-fan" aria-hidden="true">
      {imgs.map((img, i) => (
        <span key={img.id} className={`lux-fan__card lux-fan__card--${i + 1}`}><Pic img={img} /></span>
      ))}
    </div>
  );
}

/** พื้นหลังภาพตัวละครเต็มจอ เบลอ+มืด (หน้า login/register) */
export function PublicBg({ ids }) {
  const imgs = ids.map(imageById).filter(Boolean);
  return (
    <div className="lux-wall" aria-hidden="true">
      {imgs.map((img) => <Pic key={img.id} img={img} className="lux-wall__img" />)}
    </div>
  );
}

/** แบนเนอร์ใหญ่ (carousel) ตัวละครแนะนำ — crossfade, เลื่อนอัตโนมัติ 6 วิ (หยุดเมื่อ hover/focus หรือผู้ใช้ปิดแอนิเมชัน) */
export function FeaturedBanner({ items, userName }) {
  const list = (items || []).slice(0, 5);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const wrap = useRef(null);
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const n = list.length;
  const go = useCallback((k) => setI((k + n) % n), [n]);
  useEffect(() => {
    if (n < 2 || paused || reduce) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % n), 6000);
    return () => clearInterval(t);
  }, [n, paused, reduce]);
  if (!n) return null;
  return (
    <section className="lux-banner" ref={wrap} aria-roledescription="carousel" aria-label="ตัวละครแนะนำ"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      {list.map((c, k) => {
        const fb = imageFor(c.id || c.name);
        const src = sizedSrc(c.image || fb.file, "full");
        const tags = (c.tags || []).filter((t) => t !== "PG" && !/^ธง/.test(t)).slice(0, 3);
        return (
          <article key={c.id} className={`lux-banner__slide${k === i ? " is-on" : ""}`} aria-hidden={k === i ? undefined : "true"} aria-roledescription="slide" aria-label={`${k + 1} จาก ${n}`} style={{ background: fb.color }}>
            <img className="lux-banner__img" src={src} alt="" width="853" height="1280" loading={k === 0 ? "eager" : "lazy"} decoding="async" />
            <div className="lux-banner__shade" />
            <div className="lux-banner__body">
              <span className="lux-eyebrow">ตัวละครแนะนำ</span>
              <h2 className="lux-banner__name">{c.name}</h2>
              <p className="lux-banner__tag clamp-2">{personalize(c.tagline, userName)}</p>
              <div className="lux-banner__row">
                {tags.map((t) => <span key={t} className="lux-chip">{t}</span>)}
                <span className="lux-banner__stat"><Icon name="chat" size={13} /> {fmtNum(c.messageCount)}</span>
              </div>
              <Link to={`/c/${c.id}`} className="btn btn--primary lux-banner__cta" tabIndex={k === i ? 0 : -1}>เริ่มคุยเลย</Link>
            </div>
          </article>
        );
      })}
      {n > 1 ? (
        <>
          <button type="button" className="lux-banner__nav lux-banner__nav--prev" aria-label="ตัวละครก่อนหน้า" onClick={() => go(i - 1)}><Icon name="back" size={18} /></button>
          <button type="button" className="lux-banner__nav lux-banner__nav--next" aria-label="ตัวละครถัดไป" onClick={() => go(i + 1)}><Icon name="back" size={18} /></button>
          <div className="lux-banner__dots" role="tablist" aria-label="เลือกสไลด์">
            {list.map((c, k) => <button key={c.id} type="button" role="tab" aria-selected={k === i} aria-label={`สไลด์ ${k + 1}: ${c.name}`} className={k === i ? "is-on" : ""} onClick={() => go(k)} />)}
          </div>
        </>
      ) : null}
    </section>
  );
}
